// ifs_zf (NK_Plugin_Authentication) names the reason for ending an App session in X-Auth-Denied-Reason;
// the login page shows a message for the reasons listed here (src/pages/login/loginPageState.ts).
export const IP_RESTRICTED_AUTH_DENIED_REASON = 'ip_restricted';

export type AuthDeniedReason = typeof IP_RESTRICTED_AUTH_DENIED_REASON;

export type ResponseWithHeaders = { headers?: unknown } | null | undefined;

export type AppSessionEnders = {
	endLocalSession: () => Promise<void>;
	logout: () => Promise<void>;
	markLoggedOut: () => void;
};

const authDeniedReasonOf = (response: ResponseWithHeaders): null | string => {
	if (
		response === null ||
		response === undefined ||
		typeof response.headers !== 'object' ||
		response.headers === null
	) {
		return null;
	}

	const value = (response.headers as Record<string, unknown>)['x-auth-denied-reason'];

	return typeof value === 'string' ? value.trim() : null;
};

// The reason is set once per page and never downgraded: after the revocation the cookies are gone, so later or
// parallel requests answer 401 without the header, and they must neither call POST /auth/logout nor drop the reason.
export function authDeniedReasonAfterUnauthorizedResponses(
	responses: ReadonlyArray<ResponseWithHeaders>,
	previousReason: AuthDeniedReason | null
): AuthDeniedReason | null {
	if (previousReason !== null) {
		return previousReason;
	}

	return responses.some((response) => authDeniedReasonOf(response) === IP_RESTRICTED_AUTH_DENIED_REASON)
		? IP_RESTRICTED_AUTH_DENIED_REASON
		: null;
}

// Every navigation to the login page after a 401 carries the reason in the URL: the hard logout reload
// (App.vue) loses all store state, and the login page reads the reason from the route query.
export function loginHashForAuthDeniedReason(reason: AuthDeniedReason | null): string {
	return reason === null ? '#/login' : `#/login?reason=${reason}`;
}

const withoutFragment = (url: URL): string => {
	const documentUrl = new URL(url);
	documentUrl.hash = '';

	return documentUrl.href;
};

// The logout reload (App.vue) resets all in-memory state of the ended session. After an ip_restricted denial it
// lands on the login page with the reason. From index.html without a query string that target differs only in the
// fragment, which the browser treats as an in-page navigation, so the page is reloaded explicitly; from any other
// document URL (origin root, query string) assign already loads the page, and a reload would cancel it.
export function reloadAppAfterLogout(
	location: Pick<Location, 'assign' | 'href' | 'reload'>,
	reason: AuthDeniedReason | null
): void {
	if (reason === null) {
		location.assign('./index.html');
		return;
	}

	const currentUrl = new URL(location.href);
	const target = `./index.html${loginHashForAuthDeniedReason(reason)}`;
	location.assign(target);
	if (withoutFragment(new URL(target, currentUrl)) === withoutFragment(currentUrl)) {
		location.reload();
	}
}

export function bootLoginPathForAuthDeniedReason(reason: AuthDeniedReason | null): string {
	return reason === null ? '/login' : `/login?reason=${reason}`;
}

// Only GET/HEAD are replayed after a 401, and never once ifs_zf named ip_restricted: that revocation is definitive,
// and a retry failing differently (500, network error) would rethrow before the local session end and reason redirect.
export function unauthorizedRequestIsRetryable(method: string | undefined, response: ResponseWithHeaders): boolean {
	return (method === 'GET' || method === 'HEAD') && authDeniedReasonOf(response) !== IP_RESTRICTED_AUTH_DENIED_REASON;
}

// After ifs_zf revoked the session for ip_restricted the App must not call POST /auth/logout: the Auth server
// would overwrite the stored revocation reason with 'logout', and replayed or parallel requests would lose the cause.
// The App is marked logged out even when the local session end fails: a stale loggedIn=true made LoginPage reload to
// './index.html' and drop the reason.
export async function endAppSessionForAuthDeniedReason(
	reason: AuthDeniedReason | null,
	session: AppSessionEnders
): Promise<void> {
	if (reason !== IP_RESTRICTED_AUTH_DENIED_REASON) {
		await session.logout().catch(() => undefined);
		return;
	}

	await session.endLocalSession().catch(() => undefined);
	session.markLoggedOut();
}
